/**
 * Блок «Севооборот (демо)» — общий для карточки контура и карточки хозяйства.
 *
 * Он показывает то, чего не показывает детализация размещения: КОГДА культура
 * стоит на этих гектарах. Поэтому здесь не плоский список культур, а цепочка —
 * хлопок → озимые зерновые → второстепенная, — и в ней видно, что действует
 * на дату среза, а что ещё впереди.
 *
 * Почему цепочка, а не таблица «культура — площадь»
 * -------------------------------------------------
 *
 * Плоский список подталкивает сложить площади, а складывать их нельзя: хлопок
 * 10 га и посеянная после его уборки пшеница 10 га — это те же 10 га земли, а
 * не 20. Порядок и окна — единственное, что делает эти строки различимыми, и
 * именно они здесь главные.
 *
 * Блока нет, когда сервер не прислал ни одной строки. А не прислал он их ровно
 * тогда, когда синтетику не запрашивали: своей проверки `include_demo` здесь
 * нет и быть не должно — иначе рядом с серверным правилом появилось бы второе,
 * клиентское, и однажды они разойдутся.
 */

import { Link } from "react-router-dom";
import type { CropOccupancyOut, CropTimelineBlock } from "../../api/client";
import {
  Area,
  Badge,
  Card,
  EvidenceBadge,
  InlineAlert,
  ProvenanceLine,
} from "../../components/ui";
import {
  cropLabel,
  cropRoleLabel,
  occupancyStatusLabel,
  rotationGroupLabel,
  rotationPhaseLabel,
} from "../../lib/crops";
import { formatArea, formatDate } from "../../lib/format";
import { haUnit } from "../../astra/lib/format";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import styles from "../features.module.css";

const m = defineMessages({
  title: { id: "placement.cropTimeline.title", defaultMessage: "Севооборот {year}", description: "Заголовок карточки в карточке контура/хозяйства (демо-данные): севооборот за год цикла; {year} — год; рядом бейдж «ДЕМО»" },
  demo: { id: "placement.cropTimeline.demo", defaultMessage: "ДЕМО", description: "Бейдж рядом с заголовком блока: данные синтетические; заглавными, коротко" },
  alertTitle: { id: "placement.cropTimeline.alertTitle", defaultMessage: "Синтетический севооборот — сроки объявлены допущением", description: "Заголовок предупреждения над цепочкой севооборота: данные придуманы, сроки — допущение; пояснение приходит с сервера" },
  sown: {
    id: "placement.cropTimeline.sown",
    defaultMessage: "Состояние на {date}. Засеяно за цикл {area}; это НЕ площадь земли — одни и те же гектары входят сюда столько раз, сколько культур на них сменилось за год.",
    description: "Пояснение под предупреждением: {date} — дата среза, {area} — засеянная за цикл площадь с единицей («18 га»); важно, что это не площадь земли",
  },
  contourLink: { id: "placement.cropTimeline.contourLink", defaultMessage: "контур {no}", description: "Ссылка/подпись над цепочкой культур: номер контура; строчная, {no} — номер контура" },
  part: { id: "placement.cropTimeline.part", defaultMessage: "часть {label}", description: "Бейдж: землепользование на части контура; {label} — метка части; коротко" },
  partHint: { id: "placement.cropTimeline.partHint", defaultMessage: "Землепользование на части контура", description: "Подсказка к бейджу «часть …»" },
  phase: { id: "placement.cropTimeline.phase", defaultMessage: "фаза {phase}", description: "Бейдж: фазовая группа хозяйства в двухлетнем севообороте; {phase} — буква A или B; коротко" },
  noSum: { id: "placement.cropTimeline.noSum", defaultMessage: "Площади строк складывать нельзя: соседние по времени культуры стоят на одних и тех же гектарах.", description: "Подпись (caption) таблицы севооборота: площади разных культур на одной земле нельзя суммировать" },
  colPhase: { id: "placement.cropTimeline.colPhase", defaultMessage: "Фаза цикла", description: "Заголовок колонки таблицы севооборота: фаза двухлетнего цикла (хлопок, озимые, второстепенная, отдых)" },
  colCrop: { id: "placement.cropTimeline.colCrop", defaultMessage: "Культура", description: "Заголовок колонки таблицы севооборота" },
  colArea: { id: "placement.cropTimeline.colArea", defaultMessage: "Площадь, га", description: "Заголовок числовой колонки таблицы севооборота: площадь занятости в гектарах" },
  colWindow: { id: "placement.cropTimeline.colWindow", defaultMessage: "Окно", description: "Заголовок колонки таблицы севооборота: даты «с — по»" },
  colStatus: { id: "placement.cropTimeline.colStatus", defaultMessage: "Статус", description: "Заголовок колонки таблицы севооборота: не начата / идёт / завершена" },
  colClass: { id: "placement.cropTimeline.colClass", defaultMessage: "Класс", description: "Заголовок колонки таблицы севооборота: класс достоверности данных строки (бейдж)" },
  colSource: { id: "placement.cropTimeline.colSource", defaultMessage: "Происхождение", description: "Заголовок колонки таблицы севооборота: откуда взята строка" },
  roleHint: { id: "placement.cropTimeline.roleHint", defaultMessage: "Роль культуры в двухлетнем цикле контура", description: "Подсказка к бейджу роли культуры (основная / второстепенная)" },
  windowRange: { id: "placement.cropTimeline.windowRange", defaultMessage: "{from} — {to}", description: "Окно дат; {from} и {to} — готовые даты" },
  windowPlanned: { id: "placement.cropTimeline.windowPlanned", defaultMessage: "{from} — {to} (план)", description: "Окно дат звена цепочки, когда границы плановые; в скобках пометка «план»" },
  now: { id: "placement.cropTimeline.now", defaultMessage: "сейчас", description: "Бейдж в строке севооборота: эта культура стоит на земле на дату среза; очень коротко" },
});

/** Площадь словами интерфейса: «18 га», а при отсутствии — «нет данных». */
function areaWithUnit(microha: number | null | undefined): string {
  const area = formatArea(microha);
  return area.missing ? area.text : haUnit(microha! / 1_000_000);
}
import { groupByUnit, windowBasis, windowBasisLabel } from "./timelineModel";

export interface DemoCropTimelineProps {
  blocks: ReadonlyArray<CropTimelineBlock>;
  /** Контур смотрит на свою цепочку, хозяйство — на цепочки своих контуров. */
  axis: "contour" | "actor";
  /** Поисковая строка контекста анализа для ссылок между карточками. */
  linkSearch: string;
}

export function DemoCropTimeline({
  blocks,
  axis,
  linkSearch,
}: DemoCropTimelineProps) {
  const t = useT();
  if (blocks.length === 0) return null;

  return (
    <>
      {blocks.map((block) => {
        const tracks = groupByUnit(block.rows);
        return (
          <Card
            key={block.cycle_year}
            title={
              <>
                {t(m.title, { year: block.cycle_year })} <Badge tone="demo">{t(m.demo)}</Badge>
              </>
            }
          >
            <InlineAlert title={t(m.alertTitle)}>
              {block.demo_note}
            </InlineAlert>

            <p className={styles.demoBatch}>
              {t(m.sown, {
                date: formatDate(block.as_of),
                area: areaWithUnit(block.sown_microha),
              })}
            </p>

            <div className={styles.stackTight}>
              {tracks.map((track) => (
                <div className={styles.timelineTrack} key={track.key}>
                  <div className={styles.rowWrap}>
                    {axis === "actor" ? (
                      <Link
                        to={{
                          pathname: `/contours/${track.contourId}`,
                          search: linkSearch,
                        }}
                      >
                        {t(m.contourLink, { no: track.contourNo })}
                      </Link>
                    ) : (
                      <strong>{t(m.contourLink, { no: track.contourNo })}</strong>
                    )}
                    {track.partLabel ? (
                      <Badge title={t(m.partHint)}>
                        {t(m.part, { label: track.partLabel })}
                      </Badge>
                    ) : null}
                    {track.rotationPhase ? (
                      <Badge title={rotationGroupLabel(track.rotationPhase).label}>
                        {rotationGroupLabel(track.rotationPhase).known
                          ? t(m.phase, { phase: track.rotationPhase })
                          : rotationGroupLabel(track.rotationPhase).label}
                      </Badge>
                    ) : null}
                  </div>

                  <ol className={styles.timelineChain}>
                    {track.rows.map((row) => (
                      <li className={styles.timelineLink} key={row.id}>
                        <div
                          className={
                            row.is_current
                              ? `${styles.timelineStep} ${styles.timelineStepNow}`
                              : styles.timelineStep
                          }
                        >
                          <ChainStep row={row} />
                        </div>
                      </li>
                    ))}
                  </ol>

                  <div className={styles.scrollX}>
                    <table className={styles.plainTable}>
                      <caption>
                        {t(m.noSum)}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">{t(m.colPhase)}</th>
                          <th scope="col">{t(m.colCrop)}</th>
                          <th scope="col" className={styles.num}>
                            {t(m.colArea)}
                          </th>
                          <th scope="col">{t(m.colWindow)}</th>
                          <th scope="col">{t(m.colStatus)}</th>
                          <th scope="col">{t(m.colClass)}</th>
                          <th scope="col">{t(m.colSource)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {track.rows.map((row) => (
                          <tr key={row.id}>
                            <td>{rotationPhaseLabel(row.phase).label}</td>
                            <td>
                              <div className={styles.rowWrap}>
                                <span>{cropLabel(row.crop_code).label}</span>
                                <Badge
                                  tone={row.role === "main" ? "source" : "neutral"}
                                  title={t(m.roleHint)}
                                >
                                  {cropRoleLabel(row.role).label}
                                </Badge>
                              </div>
                            </td>
                            <td className={styles.num}>
                              <Area microha={row.area_microha} unit={null} />
                            </td>
                            <td>
                              {t(m.windowRange, {
                                from: formatDate(row.effective_from),
                                to: formatDate(row.effective_to),
                              })}
                              <div className={styles.timelineBasis}>
                                {windowBasisLabel(row)}
                              </div>
                            </td>
                            <td>
                              {occupancyStatusLabel(row.status).label}
                              {row.is_current ? (
                                <div className={styles.rowWrap}>
                                  <Badge tone="attention">{t(m.now)}</Badge>
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <EvidenceBadge status={row.epistemic_status} />
                            </td>
                            <td>
                              <ProvenanceLine source={row.source} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </>
  );
}

/** Одно звено цепочки: культура, окно и признак «идёт сейчас». */
function ChainStep({ row }: { row: CropOccupancyOut }) {
  const t = useT();
  const basis = windowBasis(row);
  return (
    <>
      <span className={styles.timelineCrop}>{cropLabel(row.crop_code).label}</span>
      <span className={styles.timelineWindow}>
        {t(basis === "planned" ? m.windowPlanned : m.windowRange, {
          from: formatDate(row.effective_from),
          to: formatDate(row.effective_to),
        })}
      </span>
    </>
  );
}
